import bind from 'bind-decorator';

import { limitConcurrentCalls } from '@egr/xbox/utils/Promise';
import { Lock } from '@egr/wcf/modules/utils/async';

const AppEventCanceled: symbol = Symbol('event canceled');

class AppEvent<EventOptions> {
    /**
     * This lock must be used in order to delay the execution of the other event
     * listeners present in the listeners queue.
     */
    public readonly lock: Lock = new Lock();

    protected flags: Set<symbol> = new Set();
    protected listenerOptions: Map<symbol, unknown> = new Map();

    public get wasCanceled(): boolean {
        return this.flags.has(AppEventCanceled);
    }

    public constructor(public readonly options: EventOptions) {}

    @bind
    public cancel(): void {
        this.flags.add(AppEventCanceled);
    }

    @bind
    public addFlag(flag: symbol): void {
        this.flags.add(flag);
    }

    @bind
    public hasFlag(flag: symbol): boolean {
        return this.flags.has(flag);
    }

    /** @internal */
    public getListenerOptions(key: symbol): unknown {
        return this.listenerOptions.get(key);
    }

    /** @internal */
    public setListenerOptions(key: symbol, data: unknown): void {
        this.listenerOptions.set(key, data);
    }
}

type AppEventListener<EventOptions> = (event: AppEvent<EventOptions>) => Promise<void>;

interface AppEventListenerIndex<EventOptions> {
    priority: number;
    listener: AppEventListener<EventOptions>;
}

function sortByPriority(first: {priority: number}, second: {priority: number}): number {
    return first.priority - second.priority;
}

export class EventManager<EventOptions = void> {
    protected listenerIndex: Array<AppEventListenerIndex<EventOptions>> = [];

    /**
     * **Important**: The event listeners are executed in parallel and the handler must explicitly prevent the start of
     * the other function via the events lock, if the function must be terminated before the other handlers should be started.
     *
     * @param listener The callback which received the events
     * @param priority This value is used to influence the order in which the callbacks are started.
     * **Important**: However, these are started immediately one after the other and the termination of the previous one
     * is only awaited if the event lock is used.
     *
     * @returns The returned callback can be used to remove the listener
     */
    @bind
    public addListener(listener: AppEventListener<EventOptions>, priority: number = 1024): VoidFunction {
        const entry: AppEventListenerIndex<EventOptions> = {
            listener,
            priority
        };
        this.listenerIndex.push(entry);
        this.listenerIndex.sort(sortByPriority);

        return this.removeIndex.bind(this, entry);
    }

    /**
     * @returns `true` if the event was successfully executed and `false` if the event was canceled by one of it`s handlers
     */
    @bind
    @limitConcurrentCalls()
    public async trigger(eventOptions: EventOptions, listenerOptionsHooks?: Array<(event: AppEvent<unknown>) => void>): Promise<boolean> {
        const event: AppEvent<EventOptions> = new AppEvent(eventOptions);
        if (listenerOptionsHooks != null) {
            for (const listenerOptionsHook of listenerOptionsHooks) {
                listenerOptionsHook(event);
            }
        }

        // Important: the JS spec ensures that it is save to use the `map` function in
        // this case and thereby avoid unwanted side affects if an new handler is added while
        // one event is still being processed.
        // Quote from the spec:
        // The range of elements processed by map is set before the first call to callbackfn.
        // Elements which are appended to the array after the call to map begins will not be visited by callbackfn
        await Promise.all(
            this.listenerIndex.map(
                (index: AppEventListenerIndex<EventOptions>): Promise<void> => {
                    return this.executeAppEventListener(event, index.listener);
                }
            )
        );

        return !event.wasCanceled;
    }

    @bind
    protected async executeAppEventListener(event: AppEvent<EventOptions>, listener: AppEventListener<EventOptions>): Promise<void> {
        if (event.lock.isLocked) {
            // Wait until the lock is released
            (await event.lock.acquireLock()).unlock();
        }

        if (!event.wasCanceled) {
            try {
                await listener(event);
            } catch (error) {
                event.cancel();
                throw error;
            }
        }
    }

    // Note: do not add optional arguments. Otherwise the `addListener` function
    // must be rewritten
    protected removeIndex(entry: AppEventListenerIndex<EventOptions>): void {
        const index: number = this.listenerIndex.indexOf(entry);
        if (index !== -1) {
            this.listenerIndex.splice(index, 1);
        }
    }
}